<template>
  <div>
    <input type="checkbox" id="audioFilter" name="audioFilter" :value="audioStore.isFilterActive"
      @input="onFilterInput" />
    <label for="audioFilter">audio filter</label>
    <input type="checkbox" id="audioAnalyzer" name="audioAnalyzer" :value="isAnalizerActive" @input="onAnalyzerInput" />
    <label for="audioAnalyzer">audio analyzer</label>
  </div>
  <div>
    <label>Frequency
      <input type="range" min="20" max="20000" :value="audioStore.filter.frequency.value" step="1"
        @input="onFrequencyInput">
    </label>

    <label>Q
      <input type="range" min="0.01" max="30" :value="audioStore.filter.Q.value" step="0.01" @input="onQInput">
    </label>
  </div>
  <div>
    <label>Zoom
      <input type="range" min="1" max="16" :value="spectogramZoom" step="0.1" @input="onZoomInput">
    </label>
    <label>Peak
      <input type="range" min="0" max="255" :value="spectogramPeak" @input="onPeakInput">
    </label>

  </div>
  <div>
    <label>Scroll
      <input type="range" min="0" max="200" :value="spectogramScroll" style="width: 300px;" @input="onScrollInput">
    </label>
  </div>
  <div>
    <!-- Waveform -->
    <canvas id="waveformCanvas" width="400" height="100" ref="waveformCanvas"></canvas>
  </div>
  <div>
    <!-- Spectogram -->
    <canvas id="spectrogramCanvas" width="400" height="100" ref="spectrogramCanvas"></canvas>
  </div>

</template>

<script setup>
import { onMounted, ref, useTemplateRef } from 'vue';
import { useAudioStore } from '../../stores/AudioStore';

const audioStore = useAudioStore()

const waveformCanvas = useTemplateRef('waveformCanvas')
const spectrogramCanvas = useTemplateRef('spectrogramCanvas')

const onFrequencyInput = e => {
  const res = audioStore.setFilterFrequency(e.target.valueAsNumber)
  e.target.valueAsNumber = res
}

const onQInput = e => {
  const res = audioStore.setFilterQ(e.target.valueAsNumber)
  e.target.valueAsNumber = res
}

const spectogramZoom = ref(1)
const spectogramScroll = ref(0)
const spectogramPeak = ref(0)

const onZoomInput = e => {
  spectogramZoom.value = e.target.valueAsNumber
}

const onPeakInput = e => {
  spectogramPeak.value = e.target.valueAsNumber
}

const onScrollInput = e => {
  spectogramScroll.value = e.target.valueAsNumber
}

let barsCtx
let specCtx
let animationId = 0

const isAnalizerActive = ref(false)

const onFilterInput = e => {
  audioStore.isFilterActive = !audioStore.isFilterActive
}

const onAnalyzerInput = e => {
  isAnalizerActive.value = !isAnalizerActive.value
  if (isAnalizerActive.value) {
    barsCtx = waveformCanvas.value.getContext("2d", { willReadFrequently: true });
    specCtx = spectrogramCanvas.value.getContext("2d", { willReadFrequently: true });
    drawVisualizers()
  }
}

function drawVisualizers() {
  animationId = requestAnimationFrame(drawVisualizers);
  if (!isAnalizerActive.value || !waveformCanvas.value) {
    cancelAnimationFrame(animationId);
    return
  }
  drawBars()
  drawSpectrogram()
}

function drawBars() {
  audioStore.analyser.getByteFrequencyData(audioStore.dataArray);

  barsCtx.fillStyle = "#000";
  barsCtx.fillRect(0, 0, waveformCanvas.value.width, waveformCanvas.value.height);

  const barWidth = (waveformCanvas.value.width / audioStore.bufferLength) * 2;
  let x = 0;

  barsCtx.fillStyle = `rgb(255,255,255)`;
  for (let i = 0; i < audioStore.bufferLength; i += 2) {
    const v = audioStore.dataArray[i] / 255;
    const h = v * waveformCanvas.value.height;

    barsCtx.fillRect(x, waveformCanvas.value.height - h, barWidth, h);
    x += barWidth;
  }

  if (audioStore.isFilterActive)
    drawFilterOverlay();
}

function drawFilterOverlay() {
  barsCtx.strokeStyle = "white";
  barsCtx.lineWidth = 2;
  barsCtx.beginPath();

  const nyquist = audioStore.audioContext.sampleRate / 2;

  for (let i = 0; i < waveformCanvas.value.width; i++) {
    const freq = (i / waveformCanvas.value.width) * nyquist;
    const response = getBandpassResponse(freq);
    const y = waveformCanvas.value.height - response * waveformCanvas.value.height;

    if (i === 0) barsCtx.moveTo(i, y);
    else barsCtx.lineTo(i, y);
  }
  barsCtx.stroke();
}

function getBandpassResponse(freq) {
  const f0 = audioStore.filter.frequency.value;
  const Q = audioStore.filter.Q.value;
  const w = freq / f0;
  return (w / Q) / Math.sqrt((1 - w * w) * (1 - w * w) + (w / Q) * (w / Q));
}

const spectrogramScrollSpeed = 1;

function drawSpectrogram() {
  audioStore.analyser.getByteFrequencyData(audioStore.dataArray);

  const imageData = specCtx.getImageData(
    spectrogramScrollSpeed, 0,
    spectrogramCanvas.value.width - spectrogramScrollSpeed,
    spectrogramCanvas.value.height
  );
  specCtx.putImageData(imageData, 0, 0);

  for (let x = 0; x < spectrogramScrollSpeed; x++) {

    for (let y = 0; y < spectrogramCanvas.value.height; y++) {

      const normalizedY = y / spectrogramCanvas.value.height;

      const freqIndex = Math.floor(
        normalizedY * audioStore.bufferLength / spectogramZoom.value + spectogramScroll.value
      );

      if (freqIndex >= audioStore.bufferLength) continue;

      let value = Math.max(audioStore.dataArray[freqIndex] - spectogramPeak.value, 0)

      const hue = value;
      const light = value / 2;

      specCtx.fillStyle = value == 0
        ? "#000"
        : `hsl(${hue},100%,${light}%)`;

      specCtx.fillRect(
        spectrogramCanvas.value.width - spectrogramScrollSpeed + x,
        spectrogramCanvas.value.height - y,
        1,
        1
      );
    }
  }
}
</script>

<style lang="css" scoped>
label {
  user-select: none;
}
</style>